import React, { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { LuRefreshCw } from "react-icons/lu";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  DataGrid,
  GridFooterContainer,
  useGridApiContext,
  useGridSelector,
  gridVisibleColumnDefinitionsSelector,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { CiFilter, CiExport } from "react-icons/ci";
import { useTheme } from "../../../../ThemeContext";
import { CustomNoRowsOverlay } from "../../../../components/common/OverlayGrid";
import { commonDataGridSx } from "../../../../components/common/commonDataGridStyles";
import Swal from "sweetalert2";
import {
  useFetchAwaitingAllocationQuery,
  useFetchInvoiceProjectsQuery,
  useFetchInvoiceSourcesQuery,
  useExportAwaitingAllocationMutation,
} from "../../../../slice/logdetailSlice";
import AgeingSummary from "./AgeingSummary";
import AwaitingAllocationFilter from "./AwaitingAllocationFilter";
import { getBudgetTypeInfo } from "../../../../utils/budgetTypeUtils";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : format(d, "dd MMM yyyy");
  } catch {
    return dateStr;
  }
};

const AwaitingStickyFooter = ({ totalAmount, hasData }) => {
  const { isDarkMode } = useTheme();
  const apiRef = useGridApiContext();
  const visibleColumns = useGridSelector(
    apiRef,
    gridVisibleColumnDefinitionsSelector
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    const rootEl = apiRef.current?.rootElementRef?.current;
    if (!rootEl) return;
    const virtualScroller = rootEl.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (!virtualScroller) return;

    const handleScroll = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = virtualScroller.scrollLeft;
      }
    };

    virtualScroller.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => {
      virtualScroller.removeEventListener("scroll", handleScroll);
    };
  }, [apiRef]);

  if (!hasData || !visibleColumns || visibleColumns.length === 0) return null;

  return (
    <GridFooterContainer
      sx={{
        backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
        borderTop: isDarkMode ? "1px solid #333" : "2px solid #e0e0e0",
        minHeight: "48px",
        height: "48px",
        p: 0,
        m: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          width: "100%",
          overflowX: "hidden",
          alignItems: "center",
          height: "48px",
        }}
      >
        {visibleColumns.map((col) => {
          let content = null;
          let color = isDarkMode ? "#f1f5f9" : "#1e293b";
          let fontWeight = 700;

          if (col.field === "source") {
            content = "Total awaiting allocation";
          } else if (col.field === "amount") {
            content = `₹${totalAmount}`;
            color = isDarkMode ? "#4ade80" : "#16a34a";
          }

          return (
            <Box
              key={col.field}
              sx={{
                width: col.computedWidth,
                minWidth: col.computedWidth,
                maxWidth: col.computedWidth,
                px: "10px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent:
                  col.align === "right" ? "flex-end" : "flex-start",
                fontSize: "13px",
                fontWeight,
                color,
                overflow: col.field === "source" ? "visible" : "hidden",
                textOverflow: col.field === "source" ? "clip" : "ellipsis",
                whiteSpace: "nowrap",
                zIndex: col.field === "source" ? 1 : 0,
              }}
            >
              {content}
            </Box>
          );
        })}
      </Box>
    </GridFooterContainer>
  );
};

const AwaitingAllocationTab = ({ setActiveTab, setViewingInvoice }) => {
  const { isDarkMode } = useTheme();
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [projectFilter, setProjectFilter] = useState("All projects");
  const [modelFilter, setModelFilter] = useState("All models");
  const [sourceFilter, setSourceFilter] = useState("All sources");
  const [ageFilter, setAgeFilter] = useState("All ages");
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const hasActiveDrawerFilters = useMemo(() => {
    return (
      (projectFilter && projectFilter !== "All projects") ||
      (modelFilter && modelFilter !== "All models") ||
      (sourceFilter && sourceFilter !== "All sources") ||
      (ageFilter && ageFilter !== "All ages")
    );
  }, [projectFilter, modelFilter, sourceFilter, ageFilter]);

  const handleClearAllFilters = () => {
    setStatusFilter("All statuses");
    setProjectFilter("All projects");
    setModelFilter("All models");
    setSourceFilter("All sources");
    setAgeFilter("All ages");
  };

  // Query parameters for API
  const queryParams = useMemo(() => {
    const params = {};
    if (statusFilter && statusFilter !== "All statuses") {
      params.status = statusFilter.toLowerCase();
    }
    if (projectFilter && projectFilter !== "All projects") {
      params.project_id = projectFilter;
    }
    if (modelFilter && modelFilter !== "All models") {
      params.model = modelFilter;
    }
    if (sourceFilter && sourceFilter !== "All sources") {
      params.source = sourceFilter;
    }
    if (ageFilter && ageFilter !== "All ages") {
      params.age = ageFilter;
    }
    return params;
  }, [statusFilter, projectFilter, modelFilter, sourceFilter, ageFilter]);

  const {
    data: apiResponse,
    isLoading,
    isFetching,
    refetch,
  } = useFetchAwaitingAllocationQuery(queryParams);

  const [exportAwaitingAllocation, { isLoading: isExporting }] =
    useExportAwaitingAllocationMutation();

  const handleExport = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (tableRows.length === 0) {
      toast.error("No data available to export");
      return;
    }

    Swal.close();

    const result = await Swal.fire({
      title: "Export Confirmation",
      text: "Are you sure you want to export awaiting allocation invoices?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Export",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#04a189",
      cancelButtonColor: "#d33",
      allowOutsideClick: true,
      allowEscapeKey: true,
      backdrop: true,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        htmlContainer: "custom-swal-html",
        icon: "custom-swal-icon",
        confirmButton: "swal-confirm-btn",
        cancelButton: "swal-cancel-btn",
      },
    });

    if (result.isConfirmed) {
      try {
        const response = await exportAwaitingAllocation(queryParams).unwrap();

        if (response?.status && response?.doc_url) {
          toast.success(
            response?.message || "Awaiting allocation exported successfully."
          );
          const link = document.createElement("a");
          link.href = response.doc_url;
          link.setAttribute("download", "");
          link.setAttribute("target", "_blank");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (response?.doc_url) {
          toast.success(
            response?.message || "Awaiting allocation exported successfully."
          );
          window.open(response.doc_url, "_blank");
        } else {
          toast.error(
            response?.message || "Export failed, no download link returned."
          );
        }
      } catch (error) {
        console.error("Awaiting allocation export error:", error);
        toast.error(
          error?.data?.message ||
            error?.message ||
            "Failed to export awaiting allocation."
        );
      }
    }
  };

  const { data: projectsResponse } = useFetchInvoiceProjectsQuery();
  const { data: sourcesResponse } = useFetchInvoiceSourcesQuery();

  const projectList = useMemo(() => {
    if (Array.isArray(projectsResponse)) return projectsResponse;
    if (Array.isArray(projectsResponse?.data)) return projectsResponse.data;
    return [];
  }, [projectsResponse]);

  const sourceList = useMemo(() => {
    if (Array.isArray(sourcesResponse)) return sourcesResponse;
    if (Array.isArray(sourcesResponse?.data)) return sourcesResponse.data;
    return [];
  }, [sourcesResponse]);

  const awaitingData = useMemo(() => apiResponse?.data || [], [apiResponse?.data]);
  const summary = useMemo(() => apiResponse?.summary || {}, [apiResponse?.summary]);
  const ageingList = useMemo(() => apiResponse?.ageing || [], [apiResponse?.ageing]);

  const handleActionClick = (row) => {
    if (row.action === "Investigate") {
      toast.warn(`Investigating receipt: ${row.invoice} (no hours logged recently)`);
    } else if (row.action === "Allocate") {
      if (setViewingInvoice && setActiveTab) {
        setViewingInvoice(row.raw || row);
        setActiveTab("invoice-queue");
      } else {
        toast.info(`Opening allocation flow for project: ${row.project || row.invoice}`);
      }
    } else if (row.action === "Map") {
      if (setViewingInvoice && setActiveTab) {
        setViewingInvoice(row.raw || row);
        setActiveTab("invoice-queue");
      } else {
        toast.info(`Mapping receipt: ${row.invoice}`);
      }
    } else {
      if (setViewingInvoice && setActiveTab) {
        setViewingInvoice(row.raw || row);
        setActiveTab("invoice-queue");
      } else {
        toast.info(`Viewing receipt: ${row.invoice}`);
      }
    }
  };

  const kpiData = useMemo(() => {
    const totalAmt = Number(summary?.total_awaiting || 0);
    const unmappedAmt = Number(summary?.unmapped || 0);
    const mappedAmt = Number(summary?.mapped_in_flight || 0);
    const inFlightCount = Number(summary?.projects_in_flight || 0);
    const maxAge = Number(summary?.oldest_receipt_days || 0);

    const oldestItem =
      awaitingData.length > 0
        ? [...awaitingData].sort(
            (a, b) => Number(b.age_days || 0) - Number(a.age_days || 0)
          )[0]
        : null;

    return {
      totalAmt: totalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      totalCount: awaitingData.length,
      unmappedAmt: unmappedAmt.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      unmappedCount: awaitingData.filter(
        (d) => (d.status || "").toLowerCase() === "unmapped"
      ).length,
      mappedAmt: mappedAmt.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      inFlightCount,
      maxAge,
      oldestInv: oldestItem?.invoice_number || (maxAge > 0 ? `${maxAge} days` : "—"),
    };
  }, [summary, awaitingData]);

  const tableRows = useMemo(() => {
    return awaitingData.map((item, idx) => {
      const age = Number(item.age_days || 0);
      const isUnmapped = (item.status || "").toLowerCase() === "unmapped";
      const budgetInfo = getBudgetTypeInfo(item.budget_type || item.model);
      const modelVal =
        item.model ||
        (budgetInfo.isTM
          ? "Time_and_m"
          : budgetInfo.isFixed
          ? "Fixed"
          : budgetInfo.isContract
          ? "Contract"
          : null);

      return {
        id: item.invoice_id || idx + 1,
        source: item.source || "—",
        invoice: item.invoice_number || "—",
        client: item.client_name || "—",
        project: item.project_title || null,
        project_id: item.project_id || null,
        budget_type: item.budget_type,
        model: modelVal,
        date: formatDate(item.received_date),
        amount: Number(item.amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        rawAmount: Number(item.amount || 0),
        ageDays: age,
        ageTag: `${age} days${age > 60 ? " ⚠" : ""}`,
        ageSeverity: age > 60 ? "danger" : age > 30 ? "warning" : "success",
        status: item.status_label || (isUnmapped ? "Unmapped" : "Mapped"),
        statusHint: item.status_note || (isUnmapped ? "Needs a project" : ""),
        action: item.action || (isUnmapped ? "Map" : "Allocate"),
        raw: item,
      };
    });
  }, [awaitingData]);

  const totalAmount = useMemo(() => {
    if (summary?.total_awaiting != null) {
      return Number(summary.total_awaiting).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    const sum = tableRows.reduce((acc, row) => acc + (row.rawAmount || 0), 0);
    return sum.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [summary, tableRows]);

  const isScrollable = tableRows.length > 10;

  const columns = useMemo(
    () => [
      {
        field: "source",
        headerName: "Source",
        flex: 0.7,
        minWidth: 75,
        renderCell: (params) => {
          const val = String(params.value || "").toUpperCase();
          if (val === "CASH" || val === "MANUAL" || val === "MANINV") {
            return <span className="badge-source-manual">MANUAL</span>;
          }
          return <span className="badge-source-tt">{params.value}</span>;
        },
      },
      {
        field: "invoice",
        headerName: "Invoice",
        flex: 0.9,
        minWidth: 110,
        renderCell: (params) => (
          <span style={{ fontSize: "13px", fontWeight: 500 }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "client",
        headerName: "Client",
        flex: 1.1,
        minWidth: 125,
      },
      {
        field: "project",
        headerName: "Project",
        flex: 1.7,
        minWidth: 195,
        renderCell: (params) => {
          if (!params.value) {
            return <span className="text-muted fst-italic">— not set —</span>;
          }
          return (
            <span style={{ fontSize: "13px" }} title={params.value}>
              {params.value}
            </span>
          );
        },
      },
      {
        field: "model",
        headerName: "Model",
        flex: 1.0,
        minWidth: 120,
        renderCell: (params) => {
          const budgetType = params.row.budget_type || params.row.raw?.budget_type;
          if (budgetType === "FIXED_COST") {
            return <span className="badge-model-fixed">Fixed Cost</span>;
          }
          if (budgetType === "TIME_AND_MATERIAL") {
            return <span className="badge-model-contract">T&M(Contract)</span>;
          }
          if (budgetType === "CONTRACT") {
            return <span className="badge-model-contract">Contract</span>;
          }
          return <span className="text-muted">—</span>;
        },
      },
      {
        field: "date",
        headerName: "Received",
        flex: 0.9,
        minWidth: 95,
      },
      {
        field: "amount",
        headerName: "Amount",
        flex: 1.0,
        minWidth: 105,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: isDarkMode ? "#f8fafc" : "#1e293b",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            ₹{params.value}
          </span>
        ),
      },
      {
        field: "age",
        headerName: "Age",
        flex: 0.8,
        minWidth: 95,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const age = Number(params.row.ageDays ?? 0);
          const label = `${age} ${age === 1 ? "day" : "days"}`;

          let bg, color, border, dotColor;
          if (age > 60) {
            bg = isDarkMode ? "rgba(239, 68, 68, 0.15)" : "#fef2f2";
            color = isDarkMode ? "#f87171" : "#dc2626";
            border = isDarkMode ? "1px solid rgba(239, 68, 68, 0.35)" : "1px solid #fecaca";
            dotColor = isDarkMode ? "#f87171" : "#dc2626";
          } else if (age > 30) {
            bg = isDarkMode ? "rgba(245, 158, 11, 0.15)" : "#fffbeb";
            color = isDarkMode ? "#fbbf24" : "#d97706";
            border = isDarkMode ? "1px solid rgba(245, 158, 11, 0.35)" : "1px solid #fde68a";
            dotColor = isDarkMode ? "#fbbf24" : "#d97706";
          } else {
            bg = isDarkMode ? "rgba(16, 185, 129, 0.12)" : "#ecfdf5";
            color = isDarkMode ? "#34d399" : "#059669";
            border = isDarkMode ? "1px solid rgba(16, 185, 129, 0.28)" : "1px solid #a7f3d0";
            dotColor = isDarkMode ? "#34d399" : "#10b981";
          }

          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                height: "24px",
                padding: "0 8px",
                borderRadius: "5px",
                fontSize: "12px",
                fontWeight: 600,
                backgroundColor: bg,
                color,
                border,
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: dotColor,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              {label}
            </span>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1.3,
        minWidth: 145,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {params.row.status === "Unmapped" ? (
              <span className="badge-status-unmapped">Unmapped</span>
            ) : (
              <span className="badge-status-mapped">Mapped</span>
            )}
            {params.row.statusHint && (
              <span
                style={{
                  fontSize: "11px",
                  color: "#6b7280",
                  marginTop: "3px",
                  lineHeight: "1.2",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {params.row.statusHint}
              </span>
            )}
          </div>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        flex: 0.9,
        minWidth: 105,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          if (params.row.action === "Map") {
            return (
              <button
                type="button"
                className="btn-action-map"
                onClick={() => handleActionClick(params.row)}
              >
                Map
              </button>
            );
          }
          if (params.row.action === "Investigate") {
            return (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                style={{ fontSize: "12px", height: "28px", padding: "0 10px" }}
                onClick={() => handleActionClick(params.row)}
              >
                Investigate
              </button>
            );
          }
          return (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary btn-allocate-hover"
              style={{ fontSize: "12px", height: "28px", padding: "0 10px" }}
              onClick={() => handleActionClick(params.row)}
            >
              Allocate
            </button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDarkMode]
  );

  return (
    <div className="container-fluid p-0">
      {/* 1. 5 KPI Summary Cards (Bootstrap Grid) */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Total Awaiting
            </div>
            <div className={`fs-4 fw-bold mt-1 ${isDarkMode ? "text-white" : "text-dark"}`}>
              ₹{kpiData.totalAmt}
            </div>
            <div className="text-muted small mt-1">
              Across {kpiData.totalCount} receipts
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Unmapped
            </div>
            <div className="fs-4 fw-bold mt-1" style={{ color: isDarkMode ? "#f87171" : "#dc2626" }}>
              ₹{kpiData.unmappedAmt}
            </div>
            <div className="text-muted small mt-1">
              {kpiData.unmappedCount} receipts need project
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Mapped, In Flight
            </div>
            <div className={`fs-4 fw-bold mt-1 ${isDarkMode ? "text-white" : "text-dark"}`}>
              ₹{kpiData.mappedAmt}
            </div>
            <div className="text-muted small mt-1">
              Waiting project completion
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Projects in Flight
            </div>
            <div className={`fs-4 fw-bold mt-1 ${isDarkMode ? "text-white" : "text-dark"}`}>
              {kpiData.inFlightCount}
            </div>
            <div className="text-muted small mt-1">Fixed-cost projects</div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Oldest Receipt
            </div>
            <div className="fs-4 fw-bold mt-1" style={{ color: isDarkMode ? "#f87171" : "#dc2626" }}>
              {kpiData.maxAge} days
            </div>
            <div className="text-muted small mt-1">
              {kpiData.oldestInv} (needs check)
            </div>
          </div>
        </div>
      </div>

      {/* 2. Filter Toolbar (Border Box with Status Filter & Drawer Trigger) */}
      <Box
        className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"
        sx={{
          border: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
          borderRadius: "6px",
          padding: "10px 16px",
          backgroundColor: isDarkMode ? "transparent" : "#fff",
        }}
      >
        {/* Left: Status Filter Only (as requested) + Active Filter tags */}
        <div className="d-flex align-items-center flex-wrap gap-2">
          <FormControl size="small" sx={{ width: 140 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                width: 140,
                backgroundColor: isDarkMode ? "transparent" : "#fff",
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: 140,
                  },
                },
              }}
            >
              <MenuItem value="All statuses">All statuses</MenuItem>
              <MenuItem value="unmapped">Unmapped</MenuItem>
              <MenuItem value="mapped">Mapped</MenuItem>
            </Select>
          </FormControl>

          {/* Active drawer filter pills */}
          {projectFilter && projectFilter !== "All projects" && (
            <span
              className="d-inline-flex align-items-center px-2 py-1 rounded"
              style={{
                backgroundColor: isDarkMode ? "#333" : "#f1f5f9",
                color: isDarkMode ? "#cbd5e1" : "#475569",
                fontSize: "12px",
              }}
            >
              <span className="fw-semibold me-1">Project:</span>
              {(() => {
                const found = projectList.find(
                  (p) => String(p.project_id || p.id) === String(projectFilter)
                );
                return (
                  found?.project_title ||
                  found?.title ||
                  found?.project_name ||
                  found?.name ||
                  projectFilter
                );
              })()}
              <IconButton
                size="small"
                onClick={() => setProjectFilter("All projects")}
                sx={{ p: 0.2, ms: 0.5, color: "#94a3b8" }}
              >
                <Close sx={{ fontSize: "14px" }} />
              </IconButton>
            </span>
          )}

          {modelFilter && modelFilter !== "All models" && (
            <span
              className="d-inline-flex align-items-center px-2 py-1 rounded"
              style={{
                backgroundColor: isDarkMode ? "#333" : "#f1f5f9",
                color: isDarkMode ? "#cbd5e1" : "#475569",
                fontSize: "12px",
              }}
            >
              <span className="fw-semibold me-1">Model:</span>{" "}
              {modelFilter === "Time_and_m" ? "T&M(Contract)" : modelFilter}
              <IconButton
                size="small"
                onClick={() => setModelFilter("All models")}
                sx={{ p: 0.2, ms: 0.5, color: "#94a3b8" }}
              >
                <Close sx={{ fontSize: "14px" }} />
              </IconButton>
            </span>
          )}

          {sourceFilter && sourceFilter !== "All sources" && (
            <span
              className="d-inline-flex align-items-center px-2 py-1 rounded"
              style={{
                backgroundColor: isDarkMode ? "#333" : "#f1f5f9",
                color: isDarkMode ? "#cbd5e1" : "#475569",
                fontSize: "12px",
              }}
            >
              <span className="fw-semibold me-1">Source:</span> {sourceFilter}
              <IconButton
                size="small"
                onClick={() => setSourceFilter("All sources")}
                sx={{ p: 0.2, ms: 0.5, color: "#94a3b8" }}
              >
                <Close sx={{ fontSize: "14px" }} />
              </IconButton>
            </span>
          )}

          {ageFilter && ageFilter !== "All ages" && (
            <span
              className="d-inline-flex align-items-center px-2 py-1 rounded"
              style={{
                backgroundColor: isDarkMode ? "#333" : "#f1f5f9",
                color: isDarkMode ? "#cbd5e1" : "#475569",
                fontSize: "12px",
              }}
            >
              <span className="fw-semibold me-1">Age:</span> {ageFilter}
              <IconButton
                size="small"
                onClick={() => setAgeFilter("All ages")}
                sx={{ p: 0.2, ms: 0.5, color: "#94a3b8" }}
              >
                <Close sx={{ fontSize: "14px" }} />
              </IconButton>
            </span>
          )}
        </div>

        {/* Right: Clear Filter, Filter Drawer Button, Refresh, Export */}
        <div className="d-flex align-items-center gap-2 ms-auto">
          {hasActiveDrawerFilters && (
            <button
              type="button"
              className="addmore_btn"
              onClick={handleClearAllFilters}
            >
              Clear Filter
            </button>
          )}

            {/* 1. Refresh button */}
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetch()}
                disabled={isFetching || isLoading}
                sx={{
                  backgroundColor: isDarkMode
                    ? "rgba(67, 105, 255, 0.08)"
                    : "#F1F5FF",
                  borderRadius: "50%",
                  width: "39px",
                  height: "39px",
                  "&:hover": {
                    backgroundColor: "var(--icon-hover-bg)",
                  },
                }}
              >
                {isFetching ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <LuRefreshCw className="text-rgbr-blue" size={18} />
                )}
              </IconButton>
            </Tooltip>

            {/* 2. Filter Drawer Toggle Button */}
            <Tooltip title="Filter">
              <IconButton
                onClick={() => setShowFilterDrawer(true)}
                sx={{
                  backgroundColor: isDarkMode
                    ? "rgba(67, 105, 255, 0.08)"
                    : "#F1F5FF",
                  borderRadius: "50%",
                  width: "39px",
                  height: "39px",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "var(--icon-hover-bg)",
                  },
                }}
              >
                <CiFilter className="text-rgbr-blue pro-icon-s" />
                {hasActiveDrawerFilters && (
                  <span
                    style={{
                      position: "absolute",
                      top: "7px",
                      right: "7px",
                      width: "8px",
                      height: "8px",
                      backgroundColor: "rgba(18, 183, 159, 1)",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>

            {/* 3. Export button */}
            <Tooltip title={isExporting ? "Exporting..." : "Export"}>
              <span>
                <IconButton
                  onClick={handleExport}
                  disabled={isExporting}
                  sx={{
                    backgroundColor: isDarkMode
                      ? "rgba(67, 105, 255, 0.08)"
                      : "#F1F5FF",
                    borderRadius: "50%",
                    width: "39px",
                    height: "39px",
                    "&:hover": {
                      backgroundColor: "var(--icon-hover-bg)",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.6,
                    },
                  }}
                >
                  {isExporting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CiExport className="text-rgbr-blue pro-icon-s" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
        </div>
      </Box>

      {/* 3. Receipts Awaiting Allocation Table (MUI DataGrid) */}
      <div className="card border rounded shadow-sm mb-4">
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#fff",
            height: isScrollable ? 744 : "auto",
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={tableRows}
            columns={columns}
            loading={isLoading || isFetching}
            autoHeight={!isScrollable}
            hideFooterPagination
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            rowHeight={64}
            getRowClassName={(params) => {
              if (params.row.ageDays > 60) return "row-age-danger";
              if (params.row.status === "Unmapped") return "row-status-unmapped";
              return "";
            }}
            slots={{
              footer: AwaitingStickyFooter,
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            slotProps={{
              footer: {
                totalAmount,
                hasData: Boolean(tableRows.length > 0),
              },
            }}
            sx={commonDataGridSx}
          />
        </Box>
      </div>

      {/* 4. Ageing Buckets Summary (Extracted Component) */}
      <AgeingSummary ageingList={ageingList} isLoading={isLoading || isFetching} />

      {/* 5. Warning & Invariant Note Banners */}
      <div
        className="alert alert-warning border-0 rounded p-3 mb-3"
        style={{
          backgroundColor: "#fffbeb",
          color: "#92400e",
          fontSize: "13px",
        }}
      >
        <strong>The two things this screen catches:</strong> An unmapped receipt is
        simply pending work — someone has to pick a project, and until they do
        that money is invisible everywhere. An old <em>mapped</em> receipt is
        different and more serious: ₹75,000 received 64 days ago on a project
        with almost no hours logged. Either the project stalled, timesheets are
        not being filled, or it was mapped to the wrong project.
      </div>

      <div
        className="alert alert-info border-0 rounded p-3 mb-0"
        style={{
          backgroundColor: "#eff6ff",
          color: "#1e40af",
          fontSize: "13px",
        }}
      >
        <strong>Reconciliation Invariant:</strong>{" "}
        <code>unmapped + awaiting + allocated = total received</code>, per
        source, every month. If this figure does not tie back to Zoho, something
        is being dropped.
      </div>

      {/* 6. Filter Drawer Component */}
      <AwaitingAllocationFilter
        show={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        projectList={projectList}
        sourceList={sourceList}
        statusFilter={statusFilter}
        projectFilter={projectFilter}
        modelFilter={modelFilter}
        sourceFilter={sourceFilter}
        ageFilter={ageFilter}
        isLoading={isLoading || isFetching}
        onApply={({ status, project_id, model, source, age }) => {
          setStatusFilter(status);
          setProjectFilter(project_id);
          setModelFilter(model);
          setSourceFilter(source);
          setAgeFilter(age);
          setShowFilterDrawer(false);
          toast.success("Filters applied successfully");
        }}
        onClear={() => {
          handleClearAllFilters();
        }}
      />
    </div>
  );
};

export default AwaitingAllocationTab;
